import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { Button,Dialog,DialogActions,DialogContent,DialogTitle,IconButton,Stack,TextField,Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useCallback,useContext,useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { UserContext } from '../../../context/UserContext';
import AttToken from '../../../helpers/attToken';
import { trackEventMatomo } from '../../../helpers/matomo';
import Aprovations from '../../../service/Aprovations';
import type { ChecklistReportType } from '../../../types/reports/types';
import { uploadFilesV2 } from '../../helpers/checklists';
import { ImageApproveContainer,VisuallyHiddenUploadInput } from './Styles';

const aprovationService = new Aprovations();

export default function ModalAprovation({
  openAprovation,
  setOpenAprovation,
  aprove,
  setAprove,
  checklist,
  usuarioExecutor,
  setOpenModal,
  selected,
  refetchAprovation,
}: {
  openAprovation: boolean,
  setOpenAprovation?: Function,
  aprove: string,
  setAprove?: Function,
  checklist: number,
  usuarioExecutor: string,
  setOpenModal?: Function,
  selected?: ChecklistReportType,
  refetchAprovation?: Function
}) {

  const { userData } = useContext(UserContext);
  const [comment, setComment] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedFileUrl, setSelectedFileUrl] = useState<string | null>(null);

  const { t } = useTranslation('translation');

  const close = useCallback(() => {
    trackEventMatomo('Modal de aprovação', 'click', 'button', 'reprova');
    setOpenAprovation && setOpenAprovation(false);
    setAprove && setAprove('');
  }, [setAprove, setOpenAprovation]);

  async function uploadFile() {
    try {
      if (selectedFile) {
        const generatedUID = crypto.randomUUID();
        await uploadFilesV2([selectedFile], `aprovacao/${generatedUID}`);
        return `aprovacao/${generatedUID}/${selectedFile.name}`;
      }
    } catch (err) {
      throw new Error(`Erro ao enviar arquivo: ${err}`);
    }
  }

  const { mutate, isPending } = useMutation({
    mutationKey: ['UPDATE_APROVATION'],
    mutationFn: async () => {
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error('Token not found');
          if (token) {
            const ref = await uploadFile();
            const response = await aprovationService.avaliar(
              token,
              comment,
              aprove,
              usuarioExecutor,
              checklist,
              userData?.assinatura ? true : false,
              ref || null,
            );
            if (!response) throw new Error('Erro ao atualizar aprovação');
            return response;
          }
        }, {
          pending: `${t('toast.pleaseWaitSaving')}`,
          success: `${t('toast.successSaving')}`,
          error: `${t('toast.errorSaving')}`,
        }, { toastId: 'pleaseWaitSaving' });
        return promise;
      } catch (err) {
        throw new Error(`Erro ao enviar arquivo: ${err}`);
      }
    },
    onSuccess: () => {
      trackEventMatomo('aprovações', 'sucesso', aprove === 'aprovado' ? 'aprovação' : 'reprovação', aprove === 'aprovado' ? 'formulário aprovado' : 'formulário reprovado');
      refetchAprovation && refetchAprovation();
      setOpenModal && setOpenModal(false);
    },
    onError: () => trackEventMatomo('aprovações', 'erro', aprove === 'aprovado' ? 'aprovação' : 'reprovação', 'falha ao registrar aprovação'),
  });

  return (
    <Dialog
      fullWidth
      maxWidth={'sm'}
      open={openAprovation}
      onClose={close}
      scroll={'paper'}
    >
      <DialogTitle id="scroll-dialog-title">{aprove === 'aprovado' ? t('Aprovations.approve') : t('Aprovations.reject')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => close()}
        sx={(theme) => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Stack direction='column' justifyContent='flex-start' alignItems='flex-start' width='100%' spacing={1}>
          <Typography color='textDisabled' fontWeight={600}>
            *{t("Aprovations.attention")}<br />
            *{t("Aprovations.observation")} {aprove === 'aprovado' ? t("Aprovations.aprovation") : t("Aprovations.disapproval")}.
          </Typography>
          <TextField
            fullWidth
            value={comment}
            onClick={() => trackEventMatomo('Modal de aprovação', 'click', 'input', 'adiciona comentário')}
            onChange={(e) => {
              setComment(e.target.value);
            }} placeholder={t('Aprovations.addObservation')}
          />
          <Button
            component="label"
            role={undefined}
            variant="contained"
            tabIndex={-1}
            startIcon={<CloudUploadIcon />}
          >
            {t('Aprovations.addImage')}
            <VisuallyHiddenUploadInput
              type="file"
              accept="image/*"
              onChange={(event) => {
                if (event.target.files) {
                  let file = event.target.files.item(0);
                  if (file) {
                    if (file.name.includes(' ')) {
                      file = new File([file], file.name.replaceAll(' ', '_'), { type: file.type });
                    }
                    setSelectedFile(file);
                    setSelectedFileUrl(URL.createObjectURL(file));
                  }
                }
              }}
            />
          </Button>
          {selectedFile && selectedFileUrl && (
            <>
              <ImageApproveContainer>
                <Button
                  onClick={() => {
                    setSelectedFile(null);
                    setSelectedFileUrl(null);
                  }}
                >
                  X
                </Button>
                <img src={selectedFileUrl} alt={selectedFile.name} />
              </ImageApproveContainer>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={(selected?.mandatory ? comment.length ? false : true : false) || isPending} onClick={() => mutate()}>{t("yes")}</Button>
      </DialogActions>
    </Dialog>
  );
}
